import * as React from "react";
import * as WebBrowser from "expo-web-browser";
import {
    makeRedirectUri,
    ResponseType,
    useAuthRequest,
} from "expo-auth-session";
import {
    Linking,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
    Image,
    ImageBackground,
    ActivityIndicator,
} from "react-native";
import { SPOTIFY_CLIENT_ID } from "./config";
import artists from "./artists";

WebBrowser.maybeCompleteAuthSession();

// Endpoint
const discovery = {
    authorizationEndpoint: "https://accounts.spotify.com/authorize",
    tokenEndpoint: "https://accounts.spotify.com/api/token",
};

export default function App() {
    const [accessToken, setAccessToken] = React.useState(null);
    const [likedSongs, setLikedSongs] = React.useState([]);
    const [likedArtists, setLikedArtists] = React.useState([]);
    const [topArtists, setTopArtists] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    const [request, response, promptAsync] = useAuthRequest(
        {
            responseType: ResponseType.Token,
            clientId: SPOTIFY_CLIENT_ID,
            scopes: ["user-library-read", "user-top-read"],
            usePKCE: false,
            redirectUri: makeRedirectUri({
                scheme: "glastofy",
                native: "glastofy://",
            }),
        },
        discovery
    );

    React.useEffect(() => {
        if (response?.type === "success") {
            const { access_token } = response.params;
            setAccessToken(access_token);
        }
    }, [response]);

    React.useEffect(() => {
        console.log("Access token:", accessToken);
    }, [accessToken]);

    const fetchLikedSongs = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const limit = 50; // Number of songs to fetch per request
            let offset = 0; // Starting offset

            const allLikedSongs = [];

            while (true) {
                const response = await fetch(
                    `https://api.spotify.com/v1/me/tracks?limit=${limit}&offset=${offset}`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                if (response.ok) {
                    const { items } = await response.json();
                    if (items.length === 0) {
                        break; // No more songs, break out of the loop
                    }
                    allLikedSongs.push(...items);
                    offset += limit; // Update the offset for the next request
                } else {
                    console.error(
                        "Failed to fetch liked songs:",
                        response.status
                    );
                    setError("Failed to fetch liked songs. Please try again.");
                    break;
                }
            }

            setLikedSongs(allLikedSongs);
            generateArtistsList(allLikedSongs);
        } catch (error) {
            console.error("Error occurred while fetching liked songs:", error);
            setError("Failed to fetch liked songs. Please try again.");
        }
    };

    const fetchTopArtists = async () => {
        const glastoArtists = artists;

        try {
            const limit = 50; // Number of top artists to fetch per request
            const totalLimit = 99; // Total number of top artists to fetch
            let offset = 0; // Starting offset

            const topArtistsArray = [];

            while (offset < totalLimit) {
                const response = await fetch(
                    `https://api.spotify.com/v1/me/top/artists?limit=${limit}&offset=${offset}&time_range=long_term`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                if (response.ok) {
                    const { items } = await response.json();

                    const artistNames = glastoArtists.map(
                        (artist) => artist.name
                    );

                    items.forEach((topArtist) => {
                        const artist = topArtist.name;
                        const imageUrl = topArtist.images[0].url;
                        const artistUrl = topArtist.external_urls.spotify;

                        if (artistNames.includes(artist.toUpperCase())) {
                            console.log("WOOO! We found: " + artist);
                            topArtistsArray.push({
                                artist: topArtist.name,
                                imageUrl,
                                artistUrl,
                            });
                        }
                    });

                    offset += limit - 1; // Update the offset for the next request
                } else {
                    console.error(
                        "Failed to fetch top artists:",
                        response.status
                    );
                    setError("Failed to fetch top artists. Please try again.");
                    break;
                }
            }

            setTopArtists(topArtistsArray);
        } catch (error) {
            console.error("Error occurred while fetching top artists:", error);
            setError("Failed to fetch top artists. Please try again.");
        }
    };

    const generateArtistsList = async (songs) => {
        const glastoArtists = artists;

        try {
            const artistNames = glastoArtists.map((artist) => artist.name);
            const artistsMap = new Map();

            songs.forEach((song) => {
                const artist = song.track.artists[0].name;
                const imageUrl = song.track.album.images[0].url;
                const artistUrl = song.track.artists[0].external_urls.spotify;
                const trackUrl = song.track.external_urls.spotify;
                const trackName = song.track.name;

                if (artistNames.includes(artist.toUpperCase())) {
                    if (artistsMap.has(artist)) {
                        artistsMap.get(artist).trackNames.push(trackName);
                    } else {
                        artistsMap.set(artist, {
                            trackNames: [trackName],
                            imageUrl,
                            artistUrl,
                            trackUrl,
                        });
                    }
                }
            });

            const sortedArtists = [...artistsMap.entries()].sort(
                (a, b) => b[1].trackNames.length - a[1].trackNames.length
            );

            setLikedArtists(sortedArtists);
        } catch (error) {
            console.error(
                "Error occurred while generating artists list:",
                error
            );
            setError("Failed to generate artists list. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    React.useEffect(() => {
        if (accessToken) {
            setIsLoading(true);
            fetchLikedSongs();
            fetchTopArtists();
        }
    }, [accessToken]);

    const renderArtists = () => {
        const openArtistURL = (url) => {
            if (url) {
                Linking.openURL(url);
            }
        };

        return likedArtists.map(([artist, artistData]) => {
            return (
                <View key={artist} style={styles.artistContainer}>
                    <TouchableOpacity
                        onPress={() => openArtistURL(artistData.artistUrl)}
                    >
                        <Image
                            source={{ uri: artistData.imageUrl }}
                            style={styles.artistImage}
                        />
                    </TouchableOpacity>
                    <Text style={styles.artistName}>{artist}</Text>
                    <Text style={styles.songCount}>
                        {artistData.trackNames.length} liked songs
                    </Text>
                </View>
            );
        });
    };

    const renderTopArtists = () => {
        const openArtistURL = (url) => {
            if (url) {
                Linking.openURL(url);
            }
        };

        return topArtists.map((artist) => {
            return (
                <View key={artist} style={styles.artistContainer}>
                    <TouchableOpacity
                        onPress={() => openArtistURL(artist.artistUrl)}
                    >
                        <Image
                            source={{ uri: artist.imageUrl }}
                            style={styles.artistImage}
                        />
                    </TouchableOpacity>
                    <Text style={styles.artistName}>{artist.artist}</Text>
                </View>
            );
        });
    };

    return (
        <ImageBackground
            source={require("./assets/wallpaper.png")}
            style={styles.backgroundImage}
        >
            <View style={styles.container}>
                <Text style={styles.title}>Glastofy</Text>
                {accessToken && (
                    <View style={styles.artistsContainer}>
                        {isLoading && (
                            <View style={styles.loadingContainer}>
                                <ActivityIndicator
                                    size="large"
                                    color="#FFFFFF"
                                />
                                <Text style={styles.loadingText}>
                                    Loading your liked artists...
                                </Text>
                            </View>
                        )}
                        {!isLoading && likedArtists.length > 0 && (
                            <View style={styles.artistsGrid}>
                                {renderTopArtists()}
                            </View>
                        )}
                        {!isLoading && likedArtists.length === 0 && (
                            <Text style={styles.noLikedSongsText}>
                                No liked songs
                            </Text>
                        )}
                    </View>
                )}
                {error && <Text style={styles.errorText}>{error}</Text>}
            </View>
            <View style={styles.buttonContainer}>
                {!accessToken && (
                    <TouchableOpacity
                        style={styles.button}
                        disabled={!request}
                        onPress={promptAsync}
                    >
                        <Text style={styles.buttonText}>GLASTOFY</Text>
                    </TouchableOpacity>
                )}
            </View>
        </ImageBackground>
    );
}

const styles = StyleSheet.create({
    backgroundImage: {
        flex: 1,
        resizeMode: "cover",
    },
    container: {
        marginTop: 20,
        flex: 1,
        justifyContent: "flex-start",
        alignItems: "center",
    },
    title: {
        color: "#FFFFFF",
        fontSize: 80,
        fontWeight: "bold",
    },
    loadingContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    loadingText: {
        color: "#FFFFFF",
        fontSize: 20,
        paddingTop: 20,
    },
    noLikedSongsText: {
        color: "#FFFFFF",
        fontSize: 16,
        marginTop: 20,
    },
    errorText: {
        color: "#FF0000",
        fontSize: 16,
        marginTop: 20,
    },
    buttonContainer: {
        flex: 3,
        justifyContent: "flex-start",
        alignItems: "center",
    },
    button: {
        padding: 14,
        backgroundColor: "#1DB954",
        borderRadius: 8,
        marginTop: 40,
    },
    buttonText: {
        color: "#FFFFFF",
        fontSize: 20,
        fontWeight: "bold",
    },
    artistsContainer: {
        marginTop: 16,
        flex: 1,
    },
    artistsGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
    },
    artistContainer: {
        width: 120,
        alignItems: "center",
        margin: 8,
    },
    artistImage: {
        width: 80,
        height: 80,
        borderRadius: 40,
    },
    artistName: {
        marginTop: 8,
        fontSize: 14,
        textAlign: "center",
        fontWeight: "500",
    },
    songCount: {
        fontSize: 12,
        color: "#444444",
        textAlign: "center",
    },
});
